import type { Campaign, Customer } from "../../types";
import { toE164 } from "../../../../shared-helpers/src/mobileHelpers";

// Read the existing marketing recipient payload without extending the shared
// Campaign interface or adding fields to campaign documents.
export type CampaignRecipientData = Pick<Campaign, "customers" | "customer_ids" | "customerRecipients"> & {
  directRecipientsList?: Pick<Customer, "mobile" | "mobile_code">[];
};

// Completed campaigns store their resolved audience in `customers`; the other
// fields hold the selected audience before the campaign runs.
export const getCampaignRecipientIds = (campaign: CampaignRecipientData): string[] =>
  campaign.customers?.length
    ? campaign.customers
    : campaign.customer_ids?.length
      ? campaign.customer_ids
      : campaign.customerRecipients ?? [];

export const getCampaignRecipientCount = (campaign: CampaignRecipientData): number =>
  new Set(getCampaignRecipientIds(campaign)).size + (campaign.directRecipientsList?.length ?? 0);

export const getCampaignRecipientDisplay = async (
  campaign: CampaignRecipientData,
  loadCustomer: (id: string) => Promise<Pick<Customer, "mobile" | "mobile_code"> | undefined>,
): Promise<{ count: number; mobile?: string }> => {
  const count = getCampaignRecipientCount(campaign);
  const mobiles = new Set<string>();
  const addMobile = (recipient?: { mobile?: string; mobile_code?: string }): boolean => {
    if (!recipient?.mobile?.trim()) return false;
    const number = toE164(recipient.mobile, recipient.mobile_code);
    if (!/^\+\d+$/.test(number)) return false;
    mobiles.add(number);
    return true;
  };

  for (const recipient of campaign.directRecipientsList ?? []) {
    if (!addMobile(recipient) || mobiles.size > 1) return { count };
  }

  const ids = [...new Set(getCampaignRecipientIds(campaign))];
  // Stop as soon as a second number (or an unknown recipient) makes the
  // single-number display inapplicable; don't fetch a large audience needlessly.
  for (let i = 0; i < ids.length; i += 2) {
    const customers = await Promise.all(ids.slice(i, i + 2).map(id => loadCustomer(id).catch(() => undefined)));
    for (const customer of customers) {
      if (!addMobile(customer) || mobiles.size > 1) return { count };
    }
  }

  return mobiles.size === 1 ? { count, mobile: [...mobiles][0] } : { count };
};
